/**
 * PlayerEditForm - Edit player information with team enrollment check
 */

import React, { useState, useEffect } from 'react';
import { Grid, Col, FormInput, FormSelect, FormTextArea, FormActionGroup } from './index';
import Button from './Button';
import { AlertTriangle } from 'lucide-react';

const PlayerEditForm = ({ player, isEnrolled, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    school: '',
    grade: '',
    previous_experience: '',
    medical_conditions: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (player) {
      // Normalize gender value to lowercase for matching with select options
      const genderValue = player.gender ? player.gender.toLowerCase() : '';

      setFormData({
        first_name: player.first_name || '',
        last_name: player.last_name || '',
        date_of_birth: player.date_of_birth || '',
        gender: genderValue,
        school: player.school || '',
        grade: player.grade || '',
        previous_experience: player.previous_experience || '',
        medical_conditions: player.medical_conditions || ''
      });
    }
  }, [player]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of birth is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (isEnrolled) {
      return; // Should not happen due to UI blocking
    }

    if (!validateForm()) {
      return;
    }

    onSave(formData);
  };

  if (isEnrolled) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '1rem',
        padding: '2rem',
        backgroundColor: 'var(--color-warning-light)',
        border: '2px solid var(--color-warning)',
        borderRadius: '8px',
        textAlign: 'center',
        color: 'var(--color-warning-dark)'
      }}>
        <AlertTriangle size={24} style={{ color: 'var(--color-warning)' }} />
        <div>
          <strong style={{ display: 'block', fontSize: '1.1rem', marginBottom: '0.5rem' }}>Player Cannot Be Edited</strong>
          <p style={{ margin: 0, fontSize: '0.95rem', lineHeight: '1.5', maxWidth: '500px' }}>
            This player is enrolled in a team and cannot be edited.
            Please contact your branch administrator to make any changes to player information.
          </p>
        </div>
        <Button variant="outline" onClick={onCancel}>
          Close
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <Grid columns={2} gap="1.25rem" style={{ marginBottom: '1.5rem' }}>
        <Col>
          <FormInput
            label="First Name"
            name="first_name"
            value={formData.first_name}
            onChange={handleChange}
            required
            error={errors.first_name}
          />
        </Col>

        <Col>
          <FormInput
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            onChange={handleChange}
            required
            error={errors.last_name}
          />
        </Col>

        <Col>
          <FormInput
            label="Date of Birth"
            type="date"
            name="date_of_birth"
            value={formData.date_of_birth}
            onChange={handleChange}
            required
            error={errors.date_of_birth}
          />
        </Col>

        <Col>
          <FormSelect
            label="Gender"
            name="gender"
            value={formData.gender}
            onChange={handleChange}
            required
            error={errors.gender}
          >
            <option value="">Select...</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </FormSelect>
        </Col>

        <Col>
          <FormInput
            label="School"
            name="school"
            value={formData.school}
            onChange={handleChange}
          />
        </Col>

        <Col>
          <FormInput
            label="Grade"
            name="grade"
            value={formData.grade}
            onChange={handleChange}
          />
        </Col>
      </Grid>

      <Grid columns={1} gap="1.25rem" style={{ marginBottom: '1.5rem' }}>
        <Col>
          <FormTextArea
            label="Previous Soccer Experience"
            name="previous_experience"
            value={formData.previous_experience}
            onChange={handleChange}
            rows={3}
            placeholder="Describe any previous soccer experience..."
          />
        </Col>

        <Col>
          <FormTextArea
            label="Medical Conditions / Allergies"
            name="medical_conditions"
            value={formData.medical_conditions}
            onChange={handleChange}
            rows={3}
            placeholder="List any medical conditions, allergies, or special needs..."
          />
        </Col>
      </Grid>

      <FormActionGroup
        type="submit"
        onCancel={onCancel}
        saveLabel="Save Changes"
        cancelLabel="Cancel"
      />
    </form>
  );
};

export default PlayerEditForm;
